// ATE-CR-117 — task 11 FIX: align with real backend contract.
// Backend (services/dashboard_api/.../api/credit_routes.py + credit_schemas.py):
//   * Tenant scoping is via `X-Tenant-ID: <uuid>` header (NOT a query param);
//     `x_tenant_id: UUID = Header(...)` is required on every credits route.
//   * GET /api/credits returns `{resources: ResourceUsageEntry[]}`.
//   * GET /api/credits/holds returns `{total, items, page, page_size}` and
//     accepts `include_resolved=bool` (NOT `active_only`).
//   * GET /api/credits/transactions returns the shared paginated envelope
//     `{items, total, page, page_size, total_pages}` of CreditTransactionSummary.
//   * POST /api/credits accepts `{resource, amount, note?}` body + `X-Tenant-ID`
//     header; tenant_id is NOT in the body.
import { fetchApi } from "@/api/client";

export const API_CREDITS_PATH = "/api/credits" as const;
export const API_CREDITS_HOLDS_PATH = "/api/credits/holds" as const;
export const API_CREDITS_TRANSACTIONS_PATH = "/api/credits/transactions" as const;

// ATE-CR-117 — `X-Tenant-ID` header literal. Single source of truth so every
// credits-api caller (and tests) reference the same string.
export const X_TENANT_ID_HEADER = "X-Tenant-ID" as const;

// ATE-CR-117 — Mirrors backend `ResourceUsageEntry`. All fields required.
export interface CreditBalanceResource {
  readonly resource: string;
  readonly display_name: string;
  readonly daily_limit: number;
  readonly daily_used: number;
  readonly daily_remaining: number;
  readonly bonus_balance: number;
  readonly total_remaining: number;
  readonly resets_at: string; // ISO 8601
}

// ATE-CR-117 — Mirrors backend `HoldItem`. `resolution` matches the backend
// enum (COMMITTED|RELEASED|EXPIRED) plus `null` while the hold is active.
export interface CreditHold {
  readonly id: string;
  readonly request_id: string;
  readonly resource: string;
  readonly amount: number;
  readonly source: string;
  readonly expires_at: string;
  readonly resolved_at: string | null;
  readonly resolution: "COMMITTED" | "RELEASED" | "EXPIRED" | null;
  readonly resolution_actor: string | null;
  readonly created_at: string;
}

// ATE-CR-117 — Mirrors backend `CreditTransactionSummary` (note: backend uses
// `created_by`, not `actor`, and includes `id`).
export interface CreditTransaction {
  readonly id: string;
  readonly resource: string;
  readonly amount: number;
  readonly type: string;
  readonly note: string | null;
  readonly created_by: string | null;
  readonly created_at: string; // ISO
}

// ATE-CR-117 — Body for POST /api/credits. Tenant id is sent via header, NOT
// here. `note` is optional per backend AddCreditsRequest.
export interface AddCreditRequest {
  readonly resource: string;
  readonly amount: number;
  readonly note?: string;
}

// ATE-CR-117 — backend envelope for /api/credits balance endpoint.
interface CreditUsageResponse {
  readonly resources: CreditBalanceResource[];
}

// ATE-CR-117 — backend envelope for /api/credits/holds.
interface CreditHoldsResponse {
  readonly total: number;
  readonly items: CreditHold[];
  readonly page: number;
  readonly page_size: number;
}

// ATE-CR-117 — backend envelope for /api/credits/transactions (PaginatedResponse).
interface CreditTransactionsResponse {
  readonly items: CreditTransaction[];
  readonly total: number;
  readonly page: number;
  readonly page_size: number;
  readonly total_pages: number;
}

// ATE-CR-117 — task 11 FIX: send X-Tenant-ID header (was tenant_id query param)
// and unwrap `{resources}` envelope.
export async function listCreditBalance(
  tenantId: string,
  signal?: AbortSignal,
): Promise<CreditBalanceResource[]> {
  const response = await fetchApi<CreditUsageResponse>(API_CREDITS_PATH, {
    signal,
    headers: { [X_TENANT_ID_HEADER]: tenantId },
  });
  return response.resources;
}

// ATE-CR-117 — task 11 FIX: send X-Tenant-ID header, rename active_only →
// include_resolved per backend, unwrap `{items}` envelope. The activeOnly UX
// label is preserved at the consumer (HoldsTab) — this layer translates it to
// the backend's `include_resolved` query param.
export async function listCreditHolds(
  tenantId: string,
  opts?: { activeOnly?: boolean; signal?: AbortSignal },
): Promise<CreditHold[]> {
  // activeOnly=true → include_resolved=false (default). activeOnly=false →
  // include_resolved=true (show all). Default to active-only.
  const includeResolved = opts?.activeOnly === false;
  const qs = new URLSearchParams();
  if (includeResolved) qs.set("include_resolved", "true");
  const path = qs.toString()
    ? `${API_CREDITS_HOLDS_PATH}?${qs.toString()}`
    : API_CREDITS_HOLDS_PATH;
  const response = await fetchApi<CreditHoldsResponse>(path, {
    signal: opts?.signal,
    headers: { [X_TENANT_ID_HEADER]: tenantId },
  });
  return response.items;
}

// ATE-CR-117 — task 11 FIX: send X-Tenant-ID header, unwrap `{items}` envelope.
export async function listCreditTransactions(
  tenantId: string,
  signal?: AbortSignal,
): Promise<CreditTransaction[]> {
  const response = await fetchApi<CreditTransactionsResponse>(
    API_CREDITS_TRANSACTIONS_PATH,
    { signal, headers: { [X_TENANT_ID_HEADER]: tenantId } },
  );
  return response.items;
}

// ATE-CR-117 — task 11 FIX: send X-Tenant-ID header; body now matches backend
// `AddCreditsRequest` (no tenant_id field). Caller passes tenantId separately.
export async function addCredit(
  tenantId: string,
  body: AddCreditRequest,
  signal?: AbortSignal,
): Promise<void> {
  await fetchApi<void>(API_CREDITS_PATH, {
    method: "POST",
    body: JSON.stringify(body),
    signal,
    headers: { [X_TENANT_ID_HEADER]: tenantId },
  });
}
